import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMenu, FiX, FiArrowRight, FiChevronRight, FiChevronLeft,
  FiPlus, FiStar, FiDownload, FiShield, FiAward, FiClipboard,
  FiSearch, FiPackage, FiCpu, FiChevronDown, FiCheck
} from 'react-icons/fi';
import api from '../utils/api';
import { services } from '../utils/servicesData';

/* ── Animated counter ──────────────────────────────────── */
const Counter = ({ end, suffix = '' }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let s = 0; const step = end / 80;
    const t = setInterval(() => { s += step; if (s >= end) { setN(end); clearInterval(t); } else setN(Math.floor(s)); }, 1000 / 60);
    return () => clearInterval(t);
  }, [started, end]);
  return <span ref={ref}>{n}{suffix}</span>;
};

/* ── Typing animation ──────────────────────────────────── */
const words = ['ISO Certifications', 'Food Safety Auditing', 'Contract Packgaing', 'Pest Management'];
const Typer = () => {
  const [idx, setIdx] = useState(0);
  const [txt, setTxt] = useState('');
  const [del, setDel] = useState(false);
  useEffect(() => {
    const w = words[idx];
    if (!del && txt === w) { setTimeout(() => setDel(true), 1800); return; }
    if (del && txt === '') { setDel(false); setIdx(i => (i + 1) % words.length); return; }
    const t = setTimeout(() => setTxt(p => del ? p.slice(0, -1) : w.slice(0, p.length + 1)), del ? 45 : 85);
    return () => clearTimeout(t);
  }, [txt, del, idx]);
  return (
    <span style={{ color: 'var(--gold-light)' }}>
      {txt}<span style={{ borderRight: '2px solid var(--gold-light)', marginLeft: 1, animation: 'blink .7s step-end infinite' }} />
    </span>
  );
};

/* ── Services data ─────────────────────────────────────── */
const servicesList = [
  { id: 'food-safety-auditing', icon: <FiSearch />, title: 'Food Safety Auditing', desc: 'Comprehensive audits evaluating every stage of your food safety management system against international standards.', badge: 'TOP SERVICE' },
  { id: 'regulatory-compliance', icon: <FiClipboard />, title: 'Regulatory Solutions', desc: 'Navigate FDA Ghana, GSA, and international food law requirements with full compliance confidence.' },
  { id: 'pest-control', icon: <FiShield />, title: 'Pest Control & Fumigation', desc: 'Integrated pest management and fumigation services tailored for food manufacturing and storage environments.' },
  { id: 'iso-certification', icon: <FiAward />, title: 'ISO Management Systems', desc: 'End-to-end consulting for ISO 22000, FSSC 22000, BRC and other globally recognised food safety certifications.' },
  { id: 'haccp-development', icon: <FiPackage />, title: 'Contract Packaging', desc: 'Professional packaging solutions meeting food safety standards, labelling compliance and regulatory requirements.' },
  { id: 'quality-assurance', icon: <FiCpu />, title: 'Research & Development', desc: 'Scientific R&D support for food product development, formulation, shelf-life studies and safety validation.' },
];

const heroImgs  = ['/hero1.png', '/hero2.png', '/hero3.png'];
const mobImgs   = ['/hero1.png', '/hero2.png', '/hero3.png'];

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function Home() {
  const [menuOpen, setMenuOpen]     = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const [svcOpen, setSvcOpen]       = useState(false);
  const [mobSvc, setMobSvc]         = useState(false);
  const [heroSlide, setHeroSlide]   = useState(0);
  const [mobSlide, setMobSlide]     = useState(0);
  const [testSlide, setTestSlide]   = useState(0);
  const [showTestForm, setShowTestForm] = useState(false);
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [testForm, setTestForm]     = useState({ fullName:'', company:'', position:'', text:'', rating:5, profileImage:null, companyLogo:null });
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings]     = useState({});
  const location = useLocation();
  const BACKEND  = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { const fn=()=>setScrolled(window.scrollY>24); window.addEventListener('scroll',fn); return()=>window.removeEventListener('scroll',fn); },[]);
  useEffect(() => { const t=setInterval(()=>setHeroSlide(s=>(s+1)%heroImgs.length),5000); return()=>clearInterval(t); },[]);
  useEffect(() => { const t=setInterval(()=>setMobSlide(s=>(s+1)%mobImgs.length),3500); return()=>clearInterval(t); },[]);
  useEffect(() => { setMenuOpen(false); setSvcOpen(false); setMobSvc(false); },[location]);
  useEffect(() => {
    api.get('/testimonials').then(r=>setTestimonials(r.data)).catch(()=>{});
    api.get('/settings').then(r=>setSettings(r.data)).catch(()=>{});
  },[]);

  const submitTest = async (e) => {
    e.preventDefault();
    try {
      const fd=new FormData();
      ['fullName','company','position','text','rating'].forEach(k=>fd.append(k,testForm[k]));
      if(testForm.profileImage) fd.append('profileImage',testForm.profileImage);
      if(testForm.companyLogo)  fd.append('companyLogo',testForm.companyLogo);
      await api.post('/testimonials',fd,{headers:{'Content-Type':'multipart/form-data'}});
      setTestSubmitted(true); setShowTestForm(false);
    } catch { alert('Error. Please try again.'); }
  };

  const navLinks = [
    { to:'/', label:'Home' },
    { to:'/about', label:'About' },
    { to:'/projects', label:'Portfolio' },
    { to:'/contact', label:'Contact' },
  ];

  return (
    <div style={{ background:'var(--ivory)', color:'var(--txt-body)', fontFamily:'var(--font-sans)', minHeight:'100vh' }}>

      {/* ══ NAVBAR ═══════════════════════════════════════════ */}
      <header style={{ position:'fixed', top:0, left:0, right:0, zIndex:1000, padding:'0.75rem 1.5rem', display:'flex', justifyContent:'center' }}>
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          width:'100%', maxWidth:1160,
          background: scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.85)',
          backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
          borderRadius:'var(--radius-pill)',
          padding:'0.6rem 0.75rem 0.6rem 1.25rem',
          boxShadow: scrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)',
          border:'1px solid var(--border)',
          transition:'var(--transition-slow)',
        }}>
          {/* Logo */}
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:'0.6rem', textDecoration:'none', flexShrink:0 }}>
            <img src="/logo.png" alt="SQS" style={{ height:36, objectFit:'contain' }} onError={e=>e.target.style.display='none'} />
            <div>
              <div style={{ fontWeight:800, fontSize:'0.95rem', color:'var(--navy)', lineHeight:1.1, letterSpacing:'-0.01em' }}>Smart Quality Systems</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav id="nav-desk" style={{ display:'flex', alignItems:'center', gap:'0.1rem' }}>
            {navLinks.slice(0,2).map(l=>(
              <Link key={l.to} to={l.to}
                className={`nav-link${location.pathname===l.to?' active':''}`}
                style={{ textDecoration:'none' }}>{l.label}</Link>
            ))}

            {/* Services dropdown */}
            <div style={{ position:'relative' }} onMouseEnter={()=>setSvcOpen(true)} onMouseLeave={()=>setSvcOpen(false)}>
              <Link to="/services"
                className={`nav-link${location.pathname.startsWith('/services')?' active':''}`}
                style={{ textDecoration:'none', display:'inline-flex', alignItems:'center', gap:'0.2rem' }}>
                Services <FiChevronDown style={{ fontSize:'0.8rem', transition:'transform 0.2s', transform:svcOpen?'rotate(180deg)':'none' }} />
              </Link>
              <AnimatePresence>
                {svcOpen && (
                  <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:8 }} transition={{ duration:0.15 }}
                    style={{ position:'absolute', top:'calc(100% + 8px)', left:'50%', transform:'translateX(-50%)', width:560, background:'var(--white)', borderRadius:'var(--radius-lg)', boxShadow:'var(--shadow-xl)', padding:'0.75rem', border:'1px solid var(--border)', zIndex:200, display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.25rem' }}>
                    {services.map(s=>(
                      <Link key={s.id} to={`/services/${s.id}`}
                        style={{ display:'flex', alignItems:'center', gap:'0.65rem', padding:'0.65rem 0.85rem', borderRadius:'var(--radius-md)', color:'var(--txt-body)', transition:'var(--transition)', textDecoration:'none', fontSize:'0.82rem', fontWeight:500 }}
                        onMouseEnter={e=>{ e.currentTarget.style.background='var(--ivory-2)'; e.currentTarget.style.color='var(--navy)'; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--txt-body)'; }}>
                        <span style={{ fontSize:'1rem', color:'var(--gold)', flexShrink:0 }}>{s.icon}</span>
                        {s.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(2).map(l=>(
              <Link key={l.to} to={l.to}
                className={`nav-link${location.pathname===l.to?' active':''}`}
                style={{ textDecoration:'none' }}>{l.label}</Link>
            ))}
          </nav>

          {/* Right actions */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', flexShrink:0 }}>
            <Link to="/book-service" className="btn btn-primary btn-sm btn-pill" style={{ textDecoration:'none' }}>
              Book A Service
            </Link>
            <button id="nav-ham" onClick={()=>setMenuOpen(!menuOpen)}
              style={{ display:'none', background:'none', border:'none', cursor:'pointer', color:'var(--navy)', fontSize:'1.3rem', padding:'0.3rem', alignItems:'center', justifyContent:'center', borderRadius:'8px' }}>
              {menuOpen ? <FiX/> : <FiMenu/>}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
              style={{ position:'absolute', top:'calc(100% + 4px)', left:'1rem', right:'1rem', background:'var(--white)', borderRadius:'var(--radius-lg)', padding:'1rem', boxShadow:'var(--shadow-xl)', border:'1px solid var(--border)', zIndex:999 }}>
              {navLinks.slice(0,2).map(l=>(
                <Link key={l.to} to={l.to}
                  style={{ display:'block', padding:'0.7rem 1rem', borderRadius:'var(--radius-md)', color:location.pathname===l.to?'var(--navy)':'var(--txt-body)', fontWeight:location.pathname===l.to?700:400, textDecoration:'none', background:location.pathname===l.to?'var(--ivory-2)':'transparent', marginBottom:'0.1rem' }}>
                  {l.label}
                </Link>
              ))}
              <div>
                <button onClick={()=>setMobSvc(!mobSvc)}
                  style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.7rem 1rem', borderRadius:'var(--radius-md)', color:'var(--txt-body)', background:'transparent', border:'none', cursor:'pointer', fontFamily:'var(--font-sans)', fontSize:'1rem', marginBottom:'0.1rem' }}>
                  Services <FiChevronDown style={{ transition:'transform 0.2s', transform:mobSvc?'rotate(180deg)':'none', color:'var(--gold)' }} />
                </button>
                {mobSvc && (
                  <div style={{ paddingLeft:'0.75rem', paddingBottom:'0.5rem' }}>
                    {services.map(s=>(
                      <Link key={s.id} to={`/services/${s.id}`}
                        style={{ display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.75rem', color:'var(--txt-muted)', fontSize:'0.85rem', borderRadius:'var(--radius-sm)', textDecoration:'none', marginBottom:'0.05rem' }}>
                        <span style={{ color:'var(--gold)' }}>{s.icon}</span>{s.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {navLinks.slice(2).map(l=>(
                <Link key={l.to} to={l.to}
                  style={{ display:'block', padding:'0.7rem 1rem', borderRadius:'var(--radius-md)', color:location.pathname===l.to?'var(--navy)':'var(--txt-body)', fontWeight:location.pathname===l.to?700:400, textDecoration:'none', background:location.pathname===l.to?'var(--ivory-2)':'transparent', marginBottom:'0.1rem' }}>
                  {l.label}
                </Link>
              ))}
              <Link to="/book-service" className="btn btn-primary" style={{ textDecoration:'none', display:'flex', justifyContent:'center', marginTop:'0.75rem', width:'100%' }}>
                Book a Consultation
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ══ HERO — DESKTOP (full viewport bg image) ══ */}
      <section className="hero-desktop" style={{ position:'relative', height:'80vh', minHeight:600, overflow:'hidden' }}>
        {heroImgs.map((src,i)=>(
          <div key={i} style={{ position:'absolute', inset:0, transition:'opacity 1.2s ease', opacity:i===heroSlide?1:0, zIndex:0 }}>
            <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center center' }} />
            <div style={{ position:'absolute', inset:0, background:'linear-gradient(108deg,rgba(7,26,48,0.92) 0%,rgba(11,37,69,0.85) 40%,rgba(11,37,69,0.55) 70%,rgba(0,0,0,0.18) 100%)' }} />
          </div>
        ))}

        {/* Centered hero text */}
        <div className="container" style={{ position:'relative', zIndex:2, height:'100%', display:'flex', alignItems:'center', justifyContent:'center', paddingTop:80, textAlign:'center' }}>
          <motion.div initial={{ opacity:0, y:32 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.75, ease:[0.22,1,0.36,1] }} style={{ maxWidth:780 }}>

            <h1 style={{ fontSize:'clamp(2.8rem,6vw,4.6rem)', fontWeight:900, lineHeight:1.04, marginBottom:'1.4rem', color:'#ffffff', letterSpacing:'-0.025em' }}>
              Experts in<br /><Typer />
            </h1>

            <p style={{ fontSize:'clamp(1rem,1.8vw,1.1rem)', color:'rgba(255,255,255,0.72)', lineHeight:1.8, marginBottom:'2.75rem', maxWidth:540, margin:'0 auto 2.75rem' }}>
              {settings.heroSubheading || "Ghana's premier food safety and regulatory consulting firm — helping food businesses achieve compliance, build trust, and grow with confidence."}
            </p>

            <div style={{ display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap' }}>
              <Link to="/book-service"
                style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', background:'var(--gold)', color:'var(--white)', padding:'0.9rem 2.1rem', borderRadius:'var(--radius-sm)', fontWeight:700, fontSize:'0.95rem', textDecoration:'none', border:'2px solid var(--gold)', transition:'var(--transition)', boxShadow:'0 4px 20px rgba(184,146,42,0.4)' }}
                onMouseEnter={e=>{ e.currentTarget.style.background='#9E7A1E'; e.currentTarget.style.borderColor='#9E7A1E'; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.background='var(--gold)'; e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.transform='none'; }}>
                <FiPlus /> Book a Consultation
              </Link>
              <Link to="/about"
                style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', background:'rgba(255,255,255,0.1)', color:'#ffffff', padding:'0.9rem 2.1rem', borderRadius:'var(--radius-sm)', fontWeight:600, fontSize:'0.95rem', textDecoration:'none', border:'2px solid rgba(255,255,255,0.3)', transition:'var(--transition)' }}
                onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.5)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.3)'; }}>
                Learn More <FiArrowRight />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Slide dots */}
        <div style={{ position:'absolute', bottom:'2rem', left:0, right:0, display:'flex', justifyContent:'center', gap:'0.4rem', zIndex:3 }}>
          {heroImgs.map((_,i)=>(
            <button key={i} onClick={()=>setHeroSlide(i)}
              style={{ width:i===heroSlide?28:8, height:8, borderRadius:4, background:i===heroSlide?'var(--gold-light)':'rgba(255,255,255,0.35)', border:'none', cursor:'pointer', transition:'all 0.4s', padding:0 }} />
          ))}
        </div>

        {/* ── Floating stats card — bottom right ── */}
        <motion.div
          animate={{ y:[0,-10,0] }}
          transition={{ duration:3.5, repeat:Infinity, ease:'easeInOut', delay:1 }}
          style={{
            position:'absolute', bottom:'-0.8rem', right:'3rem', zIndex:4,
            background:'rgba(255,255,255,0.14)',
            backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)',
            border:'1px solid rgba(255,255,255,0.25)',
            borderRadius:'var(--radius-lg)',
            padding:'1.4rem 2rem',
            display:'flex', alignItems:'center', gap:'2rem',
            boxShadow:'0 8px 40px rgba(0,0,0,0.25)',
          }}
          className="hero-float-card">
          {[
            { end:10, suffix:'+', label:'Experts Consultants' },
            { end:98,  suffix:'%', label:'Compliance Rate' },
            { end:15,  suffix:'+', label:'Years Experience' },
          ].map((st,i)=>(
            <React.Fragment key={i}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontWeight:900, fontSize:'1.75rem', color:'var(--gold-light)', lineHeight:1, marginBottom:'0.25rem', fontFamily:'var(--font-sans)' }}>
                  <Counter end={st.end} suffix={st.suffix} />
                </div>
                <div style={{ fontSize:'0.64rem', fontWeight:600, color:'rgba(255,255,255,0.78)', letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap' }}>
                  {st.label}
                </div>
              </div>
              {i<3 && <div style={{ width:1, height:34, background:'rgba(255,255,255,0.22)', flexShrink:0 }} />}
            </React.Fragment>
          ))}
        </motion.div>
      </section>

      {/* ══ HERO — MOBILE ══════════════════════════════════════ */}
      <section className="hero-mobile" style={{ display:'none', background:'var(--navy)', padding:'96px 1.25rem 3rem', textAlign:'center' }}>
        <div style={{ display:'inline-flex', alignItems:'center', background:'rgba(184,146,42,0.18)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:'var(--radius-sm)', padding:'0.4rem 1rem', marginBottom:'1.4rem' }}>
          <span style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--gold-light)' }}>Ghana's Premier Food Safety Consultants</span>
        </div>
        <h1 style={{ fontSize:'clamp(2rem,7vw,2.8rem)', fontWeight:900, lineHeight:1.1, marginBottom:'1rem', color:'#ffffff', letterSpacing:'-0.02em' }}>
          Experts in<br /><Typer />
        </h1>
        <p style={{ fontSize:'0.95rem', color:'rgba(255,255,255,0.68)', lineHeight:1.75, marginBottom:'2rem', maxWidth:420, margin:'0 auto 2rem' }}>
          {settings.heroSubheading || "Ghana's premier food safety and regulatory consulting firm."}
        </p>
        <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center', flexWrap:'wrap', marginBottom:'2.5rem' }}>
          <Link to="/book-service"
            style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', background:'var(--gold)', color:'var(--white)', padding:'0.85rem 1.75rem', borderRadius:'var(--radius-sm)', fontWeight:700, fontSize:'0.9rem', textDecoration:'none', border:'2px solid var(--gold)' }}>
            <FiPlus /> Book a Consultation
          </Link>
          <Link to="/about"
            style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', background:'transparent', color:'rgba(255,255,255,0.85)', padding:'0.85rem 1.75rem', borderRadius:'var(--radius-sm)', fontWeight:600, fontSize:'0.9rem', textDecoration:'none', border:'2px solid rgba(255,255,255,0.3)' }}>
            Learn More
          </Link>
        </div>
        {/* Mobile image slider */}
        <div style={{ position:'relative', borderRadius:'var(--radius-lg)', overflow:'hidden', height:210 }}>
          {mobImgs.map((src,i)=>(
            <div key={i} style={{ position:'absolute', inset:0, transition:'opacity 0.9s ease', opacity:i===mobSlide?1:0 }}>
              <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center center' }} />
            </div>
          ))}
          <div style={{ position:'absolute', bottom:'0.65rem', left:0, right:0, display:'flex', justifyContent:'center', gap:'0.35rem', zIndex:2 }}>
            {mobImgs.map((_,i)=>(
              <button key={i} onClick={()=>setMobSlide(i)}
                style={{ width:i===mobSlide?18:7, height:7, borderRadius:4, background:i===mobSlide?'var(--gold-light)':'rgba(255,255,255,0.5)', border:'none', cursor:'pointer', transition:'all 0.3s', padding:0 }} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ ABOUT / WHO WE ARE ═══════════════════════════════ */}
      <section className="section-pad section-ivory">
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5rem', alignItems:'center' }} className="about-grid">
            {/* Image grid */}
            <motion.div initial={{ opacity:0, x:-30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.875rem' }}>
                <div style={{ gridRow:'1/3', borderRadius:'var(--radius-lg)', overflow:'hidden', height:400, boxShadow:'var(--shadow-lg)' }}>
                  <img src="/hero1.png" alt="About" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                </div>
                <div style={{ borderRadius:'var(--radius-lg)', overflow:'hidden', height:194, boxShadow:'var(--shadow-md)' }}>
                  <img src="/hero3.png" alt="Team" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                </div>
                <div style={{ borderRadius:'var(--radius-lg)', background:'var(--navy)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:194, gap:'0.5rem', boxShadow:'var(--shadow-md)' }}>
                  <FiAward style={{ fontSize:'2rem', color:'var(--gold-light)' }} />
                  <div style={{ fontWeight:900, fontSize:'2.2rem', color:'var(--white)', lineHeight:1 }}>15+</div>
                  <div style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.7)', fontWeight:500 }}>Years Excellence</div>
                </div>
              </div>
            </motion.div>

            {/* Text */}
            <motion.div initial={{ opacity:0, x:30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}>
              <div style={{ display:'inline-flex', alignItems:'center', background:'var(--gold-pale)', border:'1px solid rgba(184,146,42,0.3)', borderRadius:'var(--radius-sm)', padding:'0.35rem 0.9rem', marginBottom:'1.25rem' }}>
                <span style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--gold)' }}>Who We Are</span>
              </div>
              <h2 className="section-heading" style={{ marginBottom:'0.75rem' }}>
                Crafting Tomorrow's<br />Food Safety Standards
              </h2>
              <div className="gold-bar" />
              <p style={{ color:'var(--txt-muted)', lineHeight:1.85, marginBottom:'2rem', fontSize:'0.975rem' }}>
                {settings.about_story || 'SQS is a trusted service and advisory provider specializing in Regulatory Services, ISO Management Systems Training and Certification, Research & Development(R&D), and Pest Control & Fumigation. We support organizations of all sizes in achieving regulatory compliance, operational excellence, and sustainable profitability.'}
              </p>
              {['Certified professionals with deep, hands-on food industry experience.',
                'Tailored consulting packages designed for your specific operations.',
                'Continuous monitoring to keep you compliant and growing year-round.'].map((pt,i)=>(
                <div key={i} style={{ display:'flex', gap:'0.875rem', padding:'0.875rem 0', borderBottom:'1px solid var(--border)', alignItems:'flex-start' }}>
                  <span style={{ fontWeight:900, fontSize:'0.9rem', color:'var(--gold)', minWidth:26, flexShrink:0, marginTop:1 }}>{String(i+1).padStart(2,'0')}</span>
                  <span style={{ fontSize:'0.9rem', color:'var(--txt-body)', fontWeight:500, lineHeight:1.55 }}>{pt}</span>
                </div>
              ))}
              <div style={{ display:'flex', gap:'0.875rem', marginTop:'2.25rem', flexWrap:'wrap' }}>
                <Link to="/about" className="btn btn-primary" style={{ textDecoration:'none' }}>
                  Learn More <FiArrowRight />
                </Link>
                <Link to="/book-service" className="btn btn-outline-gold" style={{ textDecoration:'none' }}>
                  Book Consultation
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ SERVICES ═════════════════════════════════════════ */}
      <section className="section-pad section-ivory2">
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:'3.5rem' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.875rem', marginBottom:'0.875rem' }}>
              <div style={{ flex:1, maxWidth:50, height:2, background:'var(--gold)', borderRadius:2 }} />
              <h2 className="section-heading">Services</h2>
              <div style={{ flex:1, maxWidth:50, height:2, background:'var(--gold)', borderRadius:2 }} />
            </div>
            <p style={{ color:'var(--txt-muted)', maxWidth:560, margin:'0 auto', lineHeight:1.75, fontSize:'0.975rem' }}>
              Comprehensive food safety and regulatory consulting services helping your business achieve the highest standards.
            </p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }} className="svc-grid">
            {servicesList.map((sv,i)=>(
              <motion.div key={sv.id} initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay:i*0.07 }}>
                <Link to={`/services/${sv.id}`} style={{ textDecoration:'none', display:'block', height:'100%' }}>
                  <div className="card" style={{ padding:'1.75rem', display:'flex', gap:'1.25rem', alignItems:'flex-start', height:'100%', boxSizing:'border-box', borderRadius:'var(--radius-lg)' }}
                    onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 12px 40px rgba(184,146,42,0.12)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='var(--shadow-sm)'; }}>
                    <div style={{ width:56, height:56, borderRadius:'var(--radius-md)', background:'var(--gold-pale)', border:'1.5px solid rgba(184,146,42,0.3)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gold)', fontSize:'1.4rem', flexShrink:0 }}>
                      {sv.icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.65rem', marginBottom:'0.5rem', flexWrap:'wrap' }}>
                        <h3 style={{ fontSize:'0.975rem', fontWeight:700, color:'var(--navy)', margin:0 }}>{sv.title}</h3>
                        
                      </div>
                      <p style={{ fontSize:'0.855rem', color:'var(--txt-muted)', lineHeight:1.7, marginBottom:'1rem' }}>{sv.desc}</p>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.3rem', color:'var(--gold)', fontSize:'0.82rem', fontWeight:600 }}>
                        Explore Details <FiChevronRight style={{ fontSize:'0.85rem' }} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign:'center', marginTop:'2.5rem' }}>
            <Link to="/services" className="btn btn-outline" style={{ textDecoration:'none' }}>
              View All Services <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ══ WHY CHOOSE US ════════════════════════════════════ */}
      <section className="section-pad section-ivory">
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5rem', alignItems:'center' }} className="why-grid">
            <motion.div initial={{ opacity:0, x:-30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}>
              <div style={{ display:'inline-flex', alignItems:'center', background:'var(--gold-pale)', border:'1px solid rgba(184,146,42,0.3)', borderRadius:'var(--radius-sm)', padding:'0.35rem 0.9rem', marginBottom:'1.25rem' }}>
                <span style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--gold)' }}>Why Partner With Us</span>
              </div>
              <h2 className="section-heading" style={{ marginBottom:'0.75rem' }}>
                The Reason Top Businesses<br />Partner With Us
              </h2>
              <div className="gold-bar" />
              <p style={{ color:'var(--txt-muted)', lineHeight:1.85, marginBottom:'2.5rem', fontSize:'0.975rem' }}>
                We combine deep regulatory expertise with practical, business-focused solutions ensuring you achieve compliance without disrupting operations.
              </p>
              {[
                { title:'Science-Led Approach', desc:'Every recommendation is grounded in evidence-based food science and internationally recognised standards.' },
                { title:'Tailored Solutions', desc:'We design consulting packages specifically for your business type, size, and regulatory environment.' },
                { title:'Proven Track Record', desc:'98% client satisfaction rate with measurable, documented compliance improvements across every engagement.' },
                { title:'Ongoing Partnership', desc:'We stay with you long after initial engagement to ensure sustained compliance success year-round.' },
              ].map((pt,i)=>(
                <div key={i} style={{ display:'flex', gap:'1rem', marginBottom:'1rem', padding:'1.1rem 1.25rem', borderRadius:'var(--radius-md)', background:'var(--white)', border:'1px solid var(--border)', boxShadow:'var(--shadow-xs)', alignItems:'flex-start', transition:'var(--transition)' }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(184,146,42,0.1)'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='var(--shadow-xs)'; }}>
                  <div style={{ width:34, height:34, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <FiArrowRight style={{ color:'var(--gold-light)', fontSize:'0.9rem' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight:700, color:'var(--navy)', marginBottom:'0.2rem', fontSize:'0.9rem' }}>{pt.title}</div>
                    <div style={{ color:'var(--txt-muted)', fontSize:'0.85rem', lineHeight:1.6 }}>{pt.desc}</div>
                  </div>
                </div>
              ))}
            </motion.div>
            <motion.div initial={{ opacity:0, x:30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}>
              <div style={{ borderRadius:'var(--radius-xl)', overflow:'hidden', boxShadow:'var(--shadow-xl)' }}>
                <img src="/hero2.png" alt="Why SQS" style={{ width:'100%', height:500, objectFit:'cover', display:'block' }} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CTA BANNER ═══════════════════════════════════════ */}
      <section style={{ padding:'3.5rem 1.5rem', background:'var(--ivory-2)' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ background:`linear-gradient(135deg, var(--navy-dark) 0%, var(--navy-light) 100%)`, borderRadius:'var(--radius-xl)', padding:'3rem 3.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'2rem', flexWrap:'wrap', position:'relative', overflow:'hidden' }}>
            {/* Decorative circles */}
            <div style={{ position:'absolute', top:-60, right:180, width:200, height:200, borderRadius:'50%', background:'rgba(201,168,76,0.06)', pointerEvents:'none' }} />
            <div style={{ position:'absolute', bottom:-40, right:80, width:140, height:140, borderRadius:'50%', background:'rgba(201,168,76,0.05)', pointerEvents:'none' }} />
            <div style={{ position:'relative' }}>
              <h3 style={{ fontSize:'clamp(1.4rem,2.5vw,1.9rem)', fontWeight:900, color:'var(--white)', marginBottom:'0.5rem', letterSpacing:'-0.01em' }}>
                Ready to Elevate Your Food Safety Standards?
              </h3>
              <p style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.9rem', margin:0 }}>
                Collaborate with our experts and bring your compliance vision to life seamlessly.
              </p>
            </div>
            <Link to="/book-service"
              style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', background:'var(--gold)', color:'var(--white)', padding:'0.9rem 2.1rem', borderRadius:'var(--radius-pill)', fontWeight:800, fontSize:'0.9rem', textDecoration:'none', border:'2px solid var(--gold)', transition:'var(--transition)', flexShrink:0, position:'relative' }}
              onMouseEnter={e=>{ e.currentTarget.style.background='#9E7A1E'; e.currentTarget.style.borderColor='#9E7A1E'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(184,146,42,0.4)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='var(--gold)'; e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
              Begin Your Project
            </Link>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ═════════════════════════════════════ */}
      <section className="section-pad section-ivory">
        <div className="container">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'3rem', flexWrap:'wrap', gap:'1rem' }}>
            <div>
              <div className="section-label">Testimonials</div>
              <h2 className="section-heading">What Clients Say</h2>
              <div className="gold-bar" style={{ marginBottom:0 }} />
            </div>
            <button onClick={()=>setShowTestForm(!showTestForm)}
              style={{ background:'transparent', color:'var(--navy)', padding:'0.6rem 1.25rem', borderRadius:'var(--radius-sm)', fontWeight:600, fontSize:'0.875rem', border:'1.5px solid var(--navy)', cursor:'pointer', fontFamily:'var(--font-sans)', transition:'var(--transition)' }}
              onMouseEnter={e=>{ e.currentTarget.style.background='var(--navy)'; e.currentTarget.style.color='var(--white)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--navy)'; }}>
              {showTestForm ? '✕ Cancel' : '+ Share Your Experience'}
            </button>
          </div>

          {showTestForm && (
            <motion.div initial={{ opacity:0, y:-16 }} animate={{ opacity:1, y:0 }}
              style={{ background:'var(--white)', borderRadius:'var(--radius-lg)', padding:'2.25rem', marginBottom:'2.5rem', border:'1px solid var(--border)', boxShadow:'var(--shadow-md)' }}>
              <h3 style={{ fontSize:'1.05rem', marginBottom:'1.5rem', color:'var(--navy)', fontWeight:700 }}>Share Your Experience</h3>
              <form onSubmit={submitTest}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" required value={testForm.fullName} onChange={e=>setTestForm({...testForm,fullName:e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Company</label><input className="form-input" value={testForm.company} onChange={e=>setTestForm({...testForm,company:e.target.value})} /></div>
                </div>
                <div className="form-group"><label className="form-label">Position</label><input className="form-input" value={testForm.position} onChange={e=>setTestForm({...testForm,position:e.target.value})} /></div>
                <div className="form-group"><label className="form-label">Your Experience *</label><textarea className="form-input" required value={testForm.text} onChange={e=>setTestForm({...testForm,text:e.target.value})} placeholder="Tell us about your experience with Smart Quality Systems..." /></div>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Your Photo</label><input type="file" accept="image/*" onChange={e=>setTestForm({...testForm,profileImage:e.target.files[0]})} style={{ padding:'0.5rem', border:'1.5px dashed var(--border-mid)', borderRadius:'var(--radius-md)', width:'100%', fontSize:'0.83rem', cursor:'pointer' }} /></div>
                  <div className="form-group"><label className="form-label">Company Logo</label><input type="file" accept="image/*" onChange={e=>setTestForm({...testForm,companyLogo:e.target.files[0]})} style={{ padding:'0.5rem', border:'1.5px dashed var(--border-mid)', borderRadius:'var(--radius-md)', width:'100%', fontSize:'0.83rem', cursor:'pointer' }} /></div>
                </div>
                <div style={{ display:'flex', gap:'0.25rem', marginBottom:'1.5rem' }}>
                  {[1,2,3,4,5].map(r=><button key={r} type="button" onClick={()=>setTestForm({...testForm,rating:r})} style={{ fontSize:'1.5rem', color:r<=testForm.rating?'var(--gold)':'var(--border-mid)', background:'none', border:'none', cursor:'pointer' }}>★</button>)}
                </div>
                <button type="submit" className="btn btn-primary" style={{ border:'none', cursor:'pointer' }}>Submit Testimonial</button>
              </form>
            </motion.div>
          )}

          {testSubmitted && (
            <div style={{ background:'#D1FAE5', padding:'1rem 1.25rem', borderRadius:'var(--radius-md)', marginBottom:'2rem', color:'#065F46', fontSize:'0.9rem', border:'1px solid #A7F3D0' }}>
              ✓ Thank you! Your testimonial has been submitted and is awaiting approval.
            </div>
          )}

          {testimonials.length > 0 ? (
            <div style={{ position:'relative' }}>
              <button onClick={()=>setTestSlide(s=>Math.max(0,s-1))}
                style={{ position:'absolute', left:-20, top:'50%', transform:'translateY(-50%)', zIndex:10, width:40, height:40, borderRadius:'50%', background:'var(--white)', border:'1.5px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', opacity:testSlide===0?0.35:1, boxShadow:'var(--shadow-md)' }}>
                <FiChevronLeft style={{ color:'var(--navy)' }} />
              </button>
              <div style={{ overflow:'hidden', margin:'0 28px' }}>
                <div style={{ display:'flex', gap:'1.5rem', transition:'transform 0.5s cubic-bezier(0.4,0,0.2,1)', transform:`translateX(calc(-${testSlide*(100/3)}% - ${testSlide*0.5}rem))` }}>
                  {testimonials.map(t=>(
                    <div key={t.id} style={{ minWidth:'calc(33.333% - 1rem)', background:'var(--white)', borderRadius:'var(--radius-lg)', padding:'2rem', border:'1px solid var(--border)', flexShrink:0, boxShadow:'var(--shadow-sm)', transition:'var(--transition)' }}
                      onMouseEnter={e=>{ e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.boxShadow='0 8px 32px rgba(184,146,42,0.1)'; e.currentTarget.style.transform='translateY(-3px)'; }}
                      onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='var(--shadow-sm)'; e.currentTarget.style.transform='none'; }}>
                      <div style={{ fontSize:'3rem', color:'var(--gold)', lineHeight:1, marginBottom:'0.5rem', opacity:0.35, fontFamily:'Georgia,serif' }}>"</div>
                      <div style={{ display:'flex', gap:'2px', marginBottom:'1rem' }}>
                        {[...Array(t.rating||5)].map((_,j)=><FiStar key={j} style={{ color:'var(--gold)', fill:'var(--gold)', fontSize:'0.85rem' }} />)}
                      </div>
                      <p style={{ color:'var(--txt-muted)', lineHeight:1.8, fontStyle:'italic', fontSize:'0.9rem', marginBottom:'1.5rem' }}>{t.text}</p>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', borderTop:'1px solid var(--border)', paddingTop:'1rem' }}>
                        {t.profile_image
                          ? <img src={`${BACKEND}${t.profile_image}`} alt={t.full_name} style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover', border:'2px solid var(--gold)' }} />
                          : <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gold-light)', fontWeight:700, fontSize:'1.1rem', flexShrink:0 }}>{t.full_name?.[0]}</div>
                        }
                        <div>
                          <div style={{ fontWeight:700, color:'var(--navy)', fontSize:'0.875rem' }}>{t.full_name}</div>
                          <div style={{ fontSize:'0.775rem', color:'var(--txt-soft)' }}>{t.position}{t.position&&t.company?', ':''}{t.company}</div>
                        </div>
                        {t.company_logo && <img src={`${BACKEND}${t.company_logo}`} alt="logo" style={{ height:26, width:'auto', objectFit:'contain', marginLeft:'auto', opacity:0.65 }} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={()=>setTestSlide(s=>Math.min(testimonials.length-3,s+1))}
                style={{ position:'absolute', right:-20, top:'50%', transform:'translateY(-50%)', zIndex:10, width:40, height:40, borderRadius:'50%', background:'var(--white)', border:'1.5px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', opacity:testSlide>=testimonials.length-3?0.35:1, boxShadow:'var(--shadow-md)' }}>
                <FiChevronRight style={{ color:'var(--navy)' }} />
              </button>
              <div style={{ display:'flex', justifyContent:'center', gap:'0.4rem', marginTop:'2rem' }}>
                {Array.from({ length:Math.max(1,testimonials.length-2) }).map((_,i)=>(
                  <button key={i} onClick={()=>setTestSlide(i)}
                    style={{ width:i===testSlide?24:8, height:8, borderRadius:4, background:i===testSlide?'var(--navy)':'var(--border-mid)', border:'none', cursor:'pointer', transition:'all 0.3s', padding:0 }} />
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'3rem', color:'var(--txt-soft)' }}>No testimonials yet. Be the first to share your experience!</div>
          )}
        </div>
      </section>

      {/* ══ PARTNERS SCROLL ══════════════════════════════════ */}
      <section style={{ padding:'2.5rem 0', background:'var(--ivory-2)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', overflow:'hidden' }}>
        <div style={{ display:'flex', gap:'3rem', animation:'slideLeft 28s linear infinite', width:'max-content', alignItems:'center' }}>
          {[...Array(13),...Array(13)].map((_,i)=>(
            <div key={i} style={{ width:120, height:60, background:'var(--white)', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', boxShadow:'var(--shadow-xs)', display:'flex', alignItems:'center', justifyContent:'center', padding:'0.5rem', flexShrink:0 }}>
              <img
                src={`/partners/partner${(i % 13) + 1}.png`}
                alt={`Partner ${(i % 13) + 1}`}
                style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', filter:'grayscale(40%)', transition:'filter 0.3s', opacity:5.5 }}
                onMouseEnter={e=>{ e.currentTarget.style.filter='grayscale(0%)'; e.currentTarget.style.opacity='1'; }}
                onMouseLeave={e=>{ e.currentTarget.style.filter='grayscale(40%)'; e.currentTarget.style.opacity='0.85'; }}
                onError={e=>e.target.style.display='none'}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ══ WHATSAPP ═════════════════════════════════════════ */}
      <button className="wa-float-btn"
        onClick={()=>window.open(`https://wa.me/${settings.whatsapp||'233246021518'}?text=Hello%20Smart%20Quality%20Systems`,'_blank','noopener')}
        style={{ position:'fixed', bottom:'2rem', right:'2rem', zIndex:9999, width:56, height:56, borderRadius:'50%', background:'#25D366', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 20px rgba(37,211,102,0.45)', animation:'waPulse 2.5s infinite' }}
        aria-label="Chat on WhatsApp">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
      </button>

      <style>{`
        @keyframes blink     { 50%  { opacity:0; } }
        @keyframes slideLeft { from { transform:translateX(0); } to { transform:translateX(-50%); } }
        @keyframes waPulse   { 0%,100%{box-shadow:0 4px 20px rgba(37,211,102,0.45)} 50%{box-shadow:0 4px 36px rgba(37,211,102,0.75)} }
        .hero-desktop { display:flex!important; flex-direction:column; }
        .hero-mobile  { display:none!important; }
        .hero-float-card { display:flex; }
        @media(max-width:900px){
          #nav-desk { display:none!important; }
          #nav-ham  { display:flex!important; }
          .about-grid,.why-grid { grid-template-columns:1fr!important; gap:2.5rem!important; }
          .svc-grid { grid-template-columns:1fr!important; }
          .hero-float-card { display:none!important; }
        }
        @media(max-width:768px){
          .hero-desktop { display:none!important; }
          .hero-mobile  { display:block!important; }
          .wa-float-btn { display:none!important; }
        }
        @media(max-width:580px){
          .svc-grid { grid-template-columns:1fr!important; }
        }
      `}</style>
    </div>
  );
}